import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environment';
import { Contract, Agreement, ContractsRequest, AdditionEsignData, PrognosisPdfRequest, AgreementAdditionExclusion, PrognosisExcelRequest } from '@model';
import { AdditionCodes } from '@enums';
import dayjs from 'dayjs';

@Injectable()
export class ContractsService {


  constructor(private http: HttpClient) {}

  getContractByMeteringPoint(meteringPointId: number): Observable<Contract> {
    return this.http
      .get<Contract>(`${environment.apiEndpoint}/contracts/${meteringPointId}`)
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

   getContractsByMeteringPointAndDateInterval(meteringPointId: number, from: Date, to: Date): Observable<Contract[]> {
    return this.http
      .post<Contract[]>(`${environment.apiEndpoint}/contracts/${meteringPointId}/dateinterval`, {from: from, to: to})
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getContractsByMeteringPointIds(contractsRequest: ContractsRequest): Observable<Contract[]> {
    return this.http
      .post<Contract[]>(`${environment.apiEndpoint}/contracts`, contractsRequest)
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getAllAgreementProducts(): Observable<Agreement[]> {
    return this.http
      .get<Agreement[]>(`${environment.apiEndpoint}/contracts/agreements/products`)
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getHasContractAdditions(payload: {meteringPointId: number, customerId: string, from: Date, to: Date, additions: AdditionCodes[]}
    ): Observable<boolean> {
      let fixedDateTo = dayjs(payload.to)
      .add(1, 'd')
      .startOf('d')
      .toDate();

    return this.http
      .post<boolean>(`${environment.apiEndpoint}/contracts/HasContractAdditions`, {
        meteringPointId: payload.meteringPointId,
        customerId: payload.customerId,
        from: dayjs(payload.from).format('YYYY-MM-DD'),
        to: dayjs(fixedDateTo).format('YYYY-MM-DD'),
        additions: payload.additions
      })
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getAgreementAdditionEsign(): Observable<AdditionEsignData[]>{
    return this.http
      .get<AdditionEsignData[]>(`${environment.apiEndpoint}/contracts/additionsesign`)
      .pipe(catchError(error => throwError(() => new Error(error.json()))));
  }

  getAgreementAdditionProductExclusions(): Observable<AgreementAdditionExclusion[]> {
    return this.http
      .get<AgreementAdditionExclusion[]>(`${environment.apiEndpoint}/contracts/additions/exclusions`)
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  exportContractsToExcel(reportKey: string, meteringPointIds: number[], contractIds: number[]): Observable<any>{
    return this.http
      .post<any>(`${environment.apiEndpoint}/reports/${reportKey}/contracts/export`, { 
        meteringPointIds: meteringPointIds, 
        contractIds: contractIds
      }, {
        responseType: 'blob' as 'json'
      })
      .pipe(catchError(error => throwError(() => new Error(error.json()))));
  }

  exportKAMPrognosisToExcel(key: string, request: PrognosisExcelRequest): Observable<any> {
    return this.http
      .post<any>(`${environment.apiEndpoint}/reports/${key}/kam`, request, {
        responseType: 'blob' as 'json'
      })
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  preparePrognosisReport(key: string, request: PrognosisPdfRequest): Observable<string>{
    return this.http.post<string>(`${environment.apiEndpoint}/reports/${key}/prognosis/download`, request);
  }

  createReportKey(): Observable<string> {
    return this.http
      .get<string>(`${environment.apiEndpoint}/contracts/key`)
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }
}
